import { ChevronRightIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, PencilIcon } from '@heroicons/react/24/outline'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { IRoadmapMilestone } from '@/MODELS/roadmapMilestone.model'
import { IRoadmapSequence } from '@/MODELS/roadmapSequence.model'

import { useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'
import Table from '@/components/Table'

const AGE_GROUPS = ['0-3', '4-5', '6-12', '13-17', '18+']
const DIAGNOSIS_STATUSES = ['pre-diagnosis', 'in evaluation process', 'diagnosed recently', 'diagnosed over a year ago']

function formatDate(date?: Date | string) {
    if (!date) return 'Unknown'
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AdminRoadmapSequencesTable() {
    const navigate = useNavigate()
    const [sequences] = useResource<IRoadmapSequence[]>(ROUTES.admin.roadmapSequence)
    const [milestones] = useResource<IRoadmapMilestone[]>(ROUTES.admin.roadmapMilestone)
    const inactiveMilestoneKeys = useMemo(() => {
        const set = new Set<string>()
        for (const m of milestones ?? []) {
            if (!m.isActive) set.add(m.key)
        }
        return set
    }, [milestones])
    const [filterAgeGroup, setFilterAgeGroup] = useState('')
    const [filterDiagnosis, setFilterDiagnosis] = useState('')
    const [search, setSearch] = useState('')

    const { activeSequences, inactiveSequences } = useMemo(() => {
        const active: IRoadmapSequence[] = []
        const inactive: IRoadmapSequence[] = []
        for (const s of sequences ?? []) {
            if (s.isActive) active.push(s)
            else inactive.push(s)
        }
        return { activeSequences: active, inactiveSequences: inactive }
    }, [sequences])

    const missingCombinations = useMemo(() => {
        if (!sequences) return []
        const activeCombos = new Set(
            activeSequences.map((s) => `${s.ageGroup}_${s.diagnosisStatus}`)
        )
        const missing: { ageGroup: string; diagnosisStatus: string }[] = []
        for (const ag of AGE_GROUPS) {
            for (const ds of DIAGNOSIS_STATUSES) {
                if (!activeCombos.has(`${ag}_${ds}`)) {
                    missing.push({ ageGroup: ag, diagnosisStatus: ds })
                }
            }
        }
        return missing
    }, [sequences, activeSequences])

    const filteredActive = activeSequences.filter((s) => {
        if (filterAgeGroup && s.ageGroup !== filterAgeGroup) return false
        if (filterDiagnosis && s.diagnosisStatus !== filterDiagnosis) return false
        if (search) {
            const q = search.toLowerCase()
            if (
                !s.ageGroup?.toLowerCase().includes(q) &&
                !s.diagnosisStatus?.toLowerCase().includes(q) &&
                !s.milestoneKeys?.some((k) => k.toLowerCase().includes(q))
            ) return false
        }
        return true
    })

    return (
        <div className='flex flex-col w-full max-w-7xl gap-5 py-10 mx-auto'>
            <AdminPageHeader
                heading='Roadmap Sequences'
                subheading={
                    <>
                        <p>Configure which milestones appear for each age group and diagnosis status combination</p>
                        <p className='text-xs text-gray-400 font-normal mt-1'>Note: Sequences cannot be deleted because they define the roadmap structure for specific user demographics. Set a sequence to Inactive to stop it from being used in future roadmap generation.</p>
                    </>
                }
                className='py-5'
                action={<Button onClick={() => navigate('/admin/roadmap-sequence/new')}>Add Sequence</Button>}
            />

            <div className='bg-white rounded-xl shadow-sm border border-neutral-200 p-4 space-y-3'>
                <div className='relative max-w-md'>
                    <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
                    <input
                        type='text'
                        placeholder='Search sequences...'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className='w-full rounded-lg border border-neutral-300 pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                </div>
                <div className='flex flex-wrap items-center gap-4'>
                    <div className='flex items-center gap-2'>
                        <label className='text-sm font-medium text-gray-700'>Diagnosis Status:</label>
                        <select
                            value={filterDiagnosis}
                            onChange={(e) => setFilterDiagnosis(e.target.value)}
                            className='rounded-lg border border-neutral-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        >
                            <option value=''>All</option>
                            {DIAGNOSIS_STATUSES.map((ds) => (
                                <option key={ds} value={ds}>{ds}</option>
                            ))}
                        </select>
                    </div>
                    <div className='flex items-center gap-2'>
                        <label className='text-sm font-medium text-gray-700'>Age Group:</label>
                        <select
                            value={filterAgeGroup}
                            onChange={(e) => setFilterAgeGroup(e.target.value)}
                            className='rounded-lg border border-neutral-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        >
                            <option value=''>All</option>
                            {AGE_GROUPS.map((ag) => (
                                <option key={ag} value={ag}>{ag}</option>
                            ))}
                        </select>
                    </div>
                    {(filterAgeGroup || filterDiagnosis) && (
                        <button
                            onClick={() => { setFilterAgeGroup(''); setFilterDiagnosis('') }}
                            className='text-sm text-blue-600 hover:text-blue-800 underline'
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            </div>

            {missingCombinations.length > 0 && (
                <div className='bg-amber-50 border border-amber-200 rounded-lg p-4'>
                    <div className='flex items-start gap-2'>
                        <ExclamationTriangleIcon className='w-5 h-5 text-amber-600 shrink-0 mt-0.5' />
                        <div>
                            <p className='text-sm font-medium text-amber-800'>
                                {missingCombinations.length} missing age/diagnosis combination{missingCombinations.length > 1 ? 's' : ''}
                            </p>
                            <p className='text-xs text-amber-700 mt-1'>
                                If a user onboards with one of these combinations, they will receive a minimal 2-milestone fallback roadmap instead of a full personalized one.
                            </p>
                            <ul className='mt-2 space-y-1'>
                                {missingCombinations.map((m) => (
                                    <li key={`${m.ageGroup}_${m.diagnosisStatus}`} className='text-xs text-amber-700'>
                                        <span className='font-medium'>{m.ageGroup}</span> / <span className='font-medium'>{m.diagnosisStatus}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            <Table
                data={filteredActive}
                hideSearch
                numberOfItemsPerPage={100}
                columns={[
                    {
                        header: 'Diagnosis Status',
                        accessor: 'diagnosisStatus',
                        cell: (s) => <div className='max-w-[200px]'>{s.diagnosisStatus}</div>,
                        sort: 'basic',
                    },
                    {
                        header: 'Age Group',
                        cell: 'ageGroup',
                        sort: 'basic',
                    },
                    {
                        header: 'Milestones',
                        cell: (s) => {
                            const deactivated = s.milestoneKeys.filter((k) => inactiveMilestoneKeys.has(k))
                            return (
                                <div className='max-w-[350px]'>
                                    <div className='truncate text-sm text-gray-500'>
                                        {s.milestoneKeys.join(' → ')}
                                    </div>
                                    {deactivated.length > 0 && (
                                        <div className='flex items-center gap-1 mt-1'>
                                            <ExclamationTriangleIcon className='w-3.5 h-3.5 text-amber-500 shrink-0' />
                                            <span className='text-xs text-amber-600'>
                                                {deactivated.length} inactive milestone{deactivated.length > 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )
                        },
                    },
                    {
                        header: 'Steps',
                        cell: (s) => s.milestoneKeys.length,
                    },
                    {
                        header: '',
                        cell: (s) => (
                            <Link
                                to={`/admin/roadmap-sequence/${s._id}`}
                                className='transition-opacity hover:opacity-70'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <PencilIcon className='w-6 h-6 shrink-0' />
                            </Link>
                        ),
                    },
                ]}
            />

            {inactiveSequences.length > 0 && (
                <details className='group'>
                    <summary className='cursor-pointer text-sm font-medium text-gray-500 flex items-center gap-2 py-2 hover:text-gray-700'>
                        <ChevronRightIcon className='w-4 h-4 transition-transform group-open:rotate-90' />
                        Inactive Sequences ({inactiveSequences.length})
                    </summary>
                    <div className='mt-2 space-y-2'>
                        {inactiveSequences.map((s) => (
                            <div
                                key={s._id}
                                className='flex items-center justify-between p-3 rounded-lg border border-neutral-200 bg-neutral-50 text-gray-500'
                            >
                                <div className='flex-1 min-w-0'>
                                    <div className='flex items-center gap-3'>
                                        <span className='text-sm font-medium text-gray-700'>{s.diagnosisStatus}</span>
                                        <span className='text-xs text-gray-400'>/</span>
                                        <span className='text-sm text-gray-600'>{s.ageGroup}</span>
                                        <span className='text-xs text-gray-400'>({s.milestoneKeys.length} milestones)</span>
                                    </div>
                                    <div className='flex items-center gap-4 mt-1 text-xs text-gray-400'>
                                        <span>Created {formatDate(s.createdAt)}</span>
                                        <span>Deactivated {formatDate(s.deactivatedAt)}</span>
                                    </div>
                                </div>
                                <Link
                                    to={`/admin/roadmap-sequence/${s._id}`}
                                    className='transition-opacity hover:opacity-70 shrink-0 ml-3'
                                >
                                    <PencilIcon className='w-5 h-5 text-gray-400' />
                                </Link>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    )
}
